import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppSettings } from "../../config/app-settings";
import { useRetrieveCampaignQuery } from "../../redux/Api/CampaignManagment/cMCampaignsApiSlice";
import Loader from "../pages/loader";
import { Card, CardHeader, CardBody } from "../../components/other/card/card";
import CampaignFormStep1 from "../../components/campaigns/steps/CampaignFormStep1";
import CampaignFormStep2 from "../../components/campaigns/steps/CampaignFormStep2";

const STEP_KEY = "campaign_edit_step";

const CampaignInfo = ({ mode = "edit" }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Campaign ID comes from the route param (/edit/:id)
  const campaignId = id ? parseInt(id, 10) : null;

  const isEdit = mode === "edit" || !!campaignId;

  const [activeStep, setActiveStep] = useState(() => {
    const saved = sessionStorage.getItem(STEP_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  useEffect(() => {
    const saved = sessionStorage.getItem(STEP_KEY);
    if (campaignId && saved && parseInt(saved, 10) !== activeStep) {
      setActiveStep(parseInt(saved, 10));
    }
  }, [campaignId, activeStep]);

  const { data: campaignData, isLoading } = useRetrieveCampaignQuery(
    { id: campaignId },
    { skip: !campaignId },
  );

  const context = useContext(AppSettings);

  useEffect(() => {
    context.setAppHeaderNone(false);
    context.setAppSidebarNone(false);
    context.setAppContentClass("");
    context.setAppThemePanal(true);

    return function cleanup() {
      context.setAppHeaderNone(true);
      context.setAppSidebarNone(true);
      context.setAppContentClass("p-0");
      context.setAppThemePanal(false);
    };
  }, []);

  const steps = [
    { id: 1, label: "Campaign Information" },
    { id: 2, label: "Customer & Platform Assignment" },
  ];

  const handleNext = (savedId) => {
    if (savedId && mode === "create") {
      sessionStorage.setItem(STEP_KEY, "2");
      setActiveStep(2);
      navigate(`/campaigns/edit/${savedId}`, { replace: true });
      return;
    }
    setActiveStep((prev) => {
      const nextStep = Math.min(prev + 1, steps.length);
      sessionStorage.setItem(STEP_KEY, String(nextStep));
      return nextStep;
    });
  };

  const handlePrev = () => {
    setActiveStep((prev) => {
      const previousStep = Math.max(prev - 1, 1);
      sessionStorage.setItem(STEP_KEY, String(previousStep));
      return previousStep;
    });
  };

  const handleFinish = () => {
    sessionStorage.removeItem(STEP_KEY);
    navigate("/campaigns/campaigns-info");
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="row">
        <Card>
          <CardHeader>
            {mode === "create" ? "CREATE CAMPAIGN" : "EDIT CAMPAIGN |"}{" "}
            {campaignData?.campaign_name}
          </CardHeader>
          <CardBody>
            <div className="nav-wizards-container p-4">
              <nav className="nav nav-wizards-1 mb-4 flex">
                {steps.map((step) => (
                  <div key={step.id} className="nav-item flex-1 text-center">
                    <div
                      className={`nav-link ${
                        step.id < activeStep
                          ? "completed"
                          : step.id === activeStep
                            ? "active"
                            : "disabled"
                      }`}
                    >
                      <div className="nav-no text-lg font-bold">{step.id}</div>
                      <div className="nav-text text-sm">{step.label}</div>
                    </div>
                  </div>
                ))}
              </nav>

              <Card>
                <CardBody className="p-6">
                  {activeStep === 1 && (
                    <div>
                      <CampaignFormStep1
                        campaign={campaignData}
                        onNext={handleNext}
                        isEdit={isEdit}
                      />
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div>
                      <CampaignFormStep2
                        campaignId={campaignId}
                        campaignName={campaignData?.campaign_name}
                        onPrevious={handlePrev}
                        onSave={handleFinish}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default CampaignInfo;
